import { useCallback, useEffect, useRef, useState, type SyntheticEvent } from "react";
import {
  KEY_STEP,
  LAYERS,
  MAX_REVEAL_MS,
  SLIDE_PX,
  SWEEP_CYCLE_MS,
  bandAt,
  clampC,
  isReady,
  markRevealed,
  onLayerReady,
  revealBandHeight,
  revealHalves,
  spotUrl,
  warm,
  type RevealLayer,
  type RevealProtect,
  type RevealRegion,
  type RevealShape,
} from "@/lib/revealKit";

// The reveal controller — a port of photohawk-app's RevealKit `useReveal`. active = { i, c }: person i,
// band centre c (fraction of their box height, continuous). shape 'tall' = portrait reveals that fill a
// phone screen. onTapOpen(i): the lightbox / example — a click / tap on a person hands off to the viewer
// instead of revealing in place. Without it (the viewer) a click / tap / press reveals the person, who
// stays up, auto-sweeps once their layers are decoded, and is scanned with the slider.
//
// Admin addition: `layerUrl` overrides where person i's layer comes from (the Settings example's
// renders are data URLs, not `?spot=…&layer=…` on a preview URL).

const prefersReducedMotion = () =>
  typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const useTouch = () => {
  const [touch, setTouch] = useState(false);
  useEffect(() => {
    setTouch(typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(hover: none)").matches);
  }, []);
  return touch;
};

const useReadyTick = () => {
  const [, setTick] = useState(0);
  useEffect(() => onLayerReady(() => setTick((t) => t + 1)), []);
};

export interface RevealActive {
  i: number;
  c: number;
}

interface Options {
  base: string | null | undefined;
  regions: RevealRegion[];
  protect?: RevealProtect | null;
  prefetch?: boolean;
  shape?: RevealShape;
  onTapOpen?: ((i: number) => void) | null;
  layerUrl?: (i: number, layer: RevealLayer) => string | undefined;
}

export const useReveal = ({ base, regions, protect, prefetch = true, shape = null, onTapOpen = null, layerUrl }: Options) => {
  useReadyTick();
  const touch = useTouch();
  const bh = revealBandHeight(protect);
  const halves = revealHalves(protect);
  const revealTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const raf = useRef<number | null>(null);
  const pendingC = useRef<number | null>(null);
  const press = useRef<{ i: number; y: number; slid: boolean } | null>(null);
  const sweepDir = useRef(1); // 1 = heading for the feet, -1 = back up to the head
  const targetRefs = useRef<Array<HTMLElement | null>>([]);
  const [active, setActive] = useState<RevealActive | null>(null);
  const [playing, setPlaying] = useState(false);
  const activeRef = useRef<RevealActive | null>(null);
  activeRef.current = active;
  const layerUrlRef = useRef(layerUrl);
  layerUrlRef.current = layerUrl;
  const hasSource = !!base || !!layerUrl;

  const urlFor = useCallback(
    (i: number, layer: RevealLayer): string | undefined =>
      layerUrlRef.current ? layerUrlRef.current(i, layer) : base ? spotUrl(base, i, layer, shape) : undefined,
    [base, shape],
  );
  const warmSubject = useCallback(
    (i: number | null | undefined) => {
      if (!hasSource || i == null) return;
      // frame first: a half is never shown before it.
      LAYERS.forEach((layer) => warm(urlFor(i, layer)));
    },
    [hasSource, urlFor],
  );

  // Everything for the active person decoded: only then is the reveal (and its controls) on screen.
  const layersReady = !!active && LAYERS.every((layer) => isReady(urlFor(active.i, layer)));
  const sweeping = playing && layersReady;
  const sweepingRef = useRef(false);
  // The first successful reveal of the session quiets the person icons' pulse on later photos.
  useEffect(() => { if (layersReady) markRevealed(); }, [layersReady]);
  sweepingRef.current = sweeping;

  const cancelFrame = () => {
    if (raf.current !== null && typeof cancelAnimationFrame !== "undefined") cancelAnimationFrame(raf.current);
    raf.current = null;
    pendingC.current = null;
  };

  // As soon as a photo opens, get the largest person's layers ready, so the first reveal is instant.
  useEffect(() => {
    cancelFrame();
    setActive(null);
    if (!prefetch || !hasSource || !regions.length) return undefined;
    const timer = setTimeout(() => warmSubject(0), 250);
    return () => clearTimeout(timer);
  }, [base, hasSource, regions.length, prefetch, warmSubject]);

  const hide = useCallback(() => {
    clearTimeout(revealTimer.current);
    cancelFrame();
    press.current = null;
    setActive(null);
  }, []);

  // Auto-hide a reveal nobody is touching — but never while it's sweeping on its own.
  const armHide = useCallback(() => {
    clearTimeout(revealTimer.current);
    if (sweepingRef.current) return;
    revealTimer.current = setTimeout(() => { cancelFrame(); press.current = null; setActive(null); }, MAX_REVEAL_MS);
  }, []);
  useEffect(() => {
    if (!active) return;
    if (sweeping) clearTimeout(revealTimer.current);
    else armHide();
    // re-evaluate when the sweep starts / stops (not on every band move)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sweeping, active && active.i]);

  // Show person i with the band at c. Moving the band on the same person is batched to one render per
  // animation frame; a new person shows at once, playing unless the viewer prefers reduced motion.
  const show = useCallback((i: number, c: number) => {
    const next = clampC(c, bh);
    const cur = activeRef.current;
    if (cur && cur.i === i) {
      armHide();
      if (typeof requestAnimationFrame === "undefined") { setActive({ i, c: next }); return; }
      pendingC.current = next;
      if (raf.current === null) {
        raf.current = requestAnimationFrame(() => {
          raf.current = null;
          const p = pendingC.current;
          pendingC.current = null;
          if (p !== null) setActive((prev) => (prev && prev.i === i && prev.c !== p ? { i, c: p } : prev));
        });
      }
      return;
    }
    cancelFrame();
    warmSubject(i);
    sweepDir.current = 1;
    setPlaying(!prefersReducedMotion());
    setActive({ i, c: next });
    armHide();
  }, [bh, warmSubject, armHide]);

  // The user takes the band: stop the sweep where it is and move it to c.
  const pause = useCallback(() => { sweepingRef.current = false; setPlaying(false); }, []);
  const play = useCallback(() => setPlaying(true), []);
  const scrub = useCallback((i: number, c: number) => { sweepingRef.current = false; setPlaying(false); show(i, c); }, [show]);

  // The auto-sweep: c = lo + span · (1 − cos 2πp) / 2 — head at p = 0, feet at p = ½, ease-in-out both
  // ways. Starts from wherever the band is (carrying on in its direction), driven by the clock.
  useEffect(() => {
    if (!sweeping || !active || typeof requestAnimationFrame === "undefined") return undefined;
    const i = active.i;
    const lo = bh / 2;
    const span = Math.max(1e-6, 1 - bh);
    const u0 = Math.max(0, Math.min(1, ((activeRef.current?.c ?? lo) - lo) / span));
    let p0 = Math.acos(1 - 2 * u0) / (2 * Math.PI); // on the way down, in [0, ½]
    if (u0 >= 0.999) p0 = 0.5;
    else if (u0 > 0.001 && sweepDir.current < 0) p0 = 1 - p0; // on the way back up
    let t0: number | null = null;
    let id = 0;
    let stopped = false;
    const tick = (now: number) => {
      if (stopped || !sweepingRef.current) return;
      if (t0 === null) t0 = now;
      const p = (p0 + (now - t0) / SWEEP_CYCLE_MS) % 1;
      sweepDir.current = p < 0.5 ? 1 : -1;
      const c = lo + span * ((1 - Math.cos(2 * Math.PI * p)) / 2);
      setActive((prev) => (prev && prev.i === i ? { i, c } : prev));
      if (!stopped) id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => { stopped = true; cancelAnimationFrame(id); };
    // the sweep restarts only when it's switched on / off or the person changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sweeping, active && active.i, bh]);

  useEffect(() => () => { clearTimeout(revealTimer.current); cancelFrame(); }, []);

  const cFor = (i: number, clientY: number) => {
    const el = targetRefs.current[i];
    if (!el) return bh / 2;
    const r = el.getBoundingClientRect();
    return bandAt(clientY, r.top, r.height, bh);
  };
  const currentC = () => (pendingC.current !== null ? pendingC.current : activeRef.current ? activeRef.current.c : bh / 2);

  // Hotspot event handlers for person i. Nothing reveals on hover / pointer movement: a mouse only warms
  // the person's layers on the way in.
  const handOff = !!onTapOpen;
  const noMenu = (e: SyntheticEvent) => e.preventDefault();
  const targetProps = (i: number) =>
    handOff
      ? {
          ref: (el: HTMLElement | null) => { targetRefs.current[i] = el; },
          "aria-label": touch ? `Show person ${i + 1} full screen` : `Show person ${i + 1} clearly, full screen`,
          className: "phx-spot-target",
          touchAction: "manipulation" as const,
          onMouseEnter: () => warmSubject(i),
          onPointerDown: (e: React.PointerEvent) => { e.stopPropagation(); warmSubject(i); },
          onTouchStart: (e: React.TouchEvent) => e.stopPropagation(),
          onClick: (e: React.MouseEvent) => { e.stopPropagation(); onTapOpen?.(i); },
          onContextMenu: noMenu,
        }
      : {
          ref: (el: HTMLElement | null) => { targetRefs.current[i] = el; },
          "aria-label": `Scan person ${i + 1}`,
          className: "phx-spot-target",
          touchAction: "none" as const,
          onMouseEnter: () => warmSubject(i),
          onPointerDown: (e: React.PointerEvent) => {
            if (e.pointerType === "mouse") return;
            e.stopPropagation();
            e.preventDefault();
            warmSubject(i);
            try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* pointer already gone */ }
            press.current = { i, y: e.clientY, slid: false };
            show(i, cFor(i, e.clientY));
          },
          onPointerMove: (e: React.PointerEvent) => {
            if (e.pointerType === "mouse" || !active || active.i !== i) return;
            e.stopPropagation();
            const pr = press.current;
            if (!pr || pr.i !== i) return;
            // A slide takes the band over (and stops the sweep); a tap leaves it sweeping.
            if (!pr.slid && Math.abs(e.clientY - pr.y) < SLIDE_PX) return;
            pr.slid = true;
            scrub(i, cFor(i, e.clientY));
          },
          onPointerUp: (e: React.PointerEvent) => { if (e.pointerType !== "mouse") { e.stopPropagation(); press.current = null; } },
          onPointerCancel: (e: React.PointerEvent) => { if (e.pointerType !== "mouse") press.current = null; },
          // A mouse click reveals the person from the head (touch already did on press).
          onClick: (e: React.MouseEvent) => { e.stopPropagation(); if (!activeRef.current || activeRef.current.i !== i) show(i, bh / 2); },
          onTouchStart: (e: React.TouchEvent) => e.stopPropagation(),
          onTouchMove: (e: React.TouchEvent) => e.stopPropagation(),
          onFocus: (e: React.FocusEvent<HTMLElement>) => { if (e.currentTarget.matches?.(":focus-visible")) show(i, bh / 2); },
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === "Enter") { e.preventDefault(); if (active && active.i === i) hide(); else show(i, bh / 2); }
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              const d = e.key === "ArrowDown" ? KEY_STEP : -KEY_STEP;
              if (active && active.i === i) scrub(i, currentC() + d);
              else show(i, bh / 2);
            }
          },
          onContextMenu: noMenu,
        };

  // The reveal window of person i in this shape.
  const winFor = (i: number) => {
    const r = regions[i];
    if (!r) return null;
    return (shape === "tall" ? r.winTall : r.win) || null;
  };

  return {
    active, show, hide, touch, handOff, targetProps, urlFor, winFor, regions, bandHeight: bh, halves,
    playing, play, pause, scrub, layersReady,
    // the slider + play / pause: once the revealed person's layers are all decoded
    controls: layersReady,
    loading: !!active && !layersReady,
  };
};

export type RevealController = ReturnType<typeof useReveal>;
